import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axiosInstance from '../../utility/axiosInstance';
import { handleAxiosError } from '../../helpers/handleAxiosError';

export interface Region {
  id: string;
  name?: string;
}

interface RegionsState {
  regions: Region[];
  currentRegion: string | null; // region id to display in navbar
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: RegionsState = {
  regions: [],
  currentRegion: null,
  status: 'idle',
  error: null,
};

export const fetchRegions = createAsyncThunk<Region[], void, { rejectValue: string }>(
  'regions/fetchRegions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/v1/identity/regions/list');
      const list: Region[] = response.data || [];
      return list;
    } catch (err) {
      return rejectWithValue(handleAxiosError(err));
    }
  }
);

const regionsSlice = createSlice({
  name: 'regions',
  initialState,
  reducers: {
    setCurrentRegion: (state, action: PayloadAction<string | null>) => {
      state.currentRegion = action.payload;
    },
    resetRegions: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRegions.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchRegions.fulfilled, (state, action: PayloadAction<Region[]>) => {
        state.status = 'succeeded';
        state.regions = action.payload;
        if (!state.currentRegion) {
          state.currentRegion = action.payload[0]?.id || null;
        }
      })
      .addCase(fetchRegions.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as string) || 'Failed to load regions';
        state.regions = [];
      });
  },
});

export const { setCurrentRegion, resetRegions } = regionsSlice.actions;
export default regionsSlice.reducer;
